import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Box, CircularProgress } from '@mui/material';

import { useNotify } from '@/app/hooks';
import type { UserRole } from '@/graphql/graphql';
import { useAuth } from './AuthProvider';

/** Where each role belongs when it lands somewhere it should not be. */
export function homePathFor(role: UserRole): string {
  if (role === 'ADMIN') return '/admin';
  if (role === 'WAITER') return '/app/floor';
  return '/app';
}

export function AuthGate() {
  const { loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return <Outlet />;
}

export function RequireAuth({
  roles,
  deniedToast,
}: {
  roles?: UserRole[];
  /** Shown once when the signed-in user lacks an allowed role. */
  deniedToast?: string;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const notify = useNotify();
  const denied = Boolean(user && roles && !roles.includes(user.role));
  const toastedDenied = useRef(false);

  useEffect(() => {
    if (!denied || !deniedToast || toastedDenied.current) return;
    toastedDenied.current = true;
    notify(deniedToast, 'info');
  }, [denied, deniedToast, notify]);

  if (loading) return <FullPageSpinner />;

  if (!user) {
    // The attempted path rides along so signing in lands where the user meant
    // to go rather than dumping them on a dashboard.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to={homePathFor(user.role)} replace />;
  }

  return <Outlet />;
}

export function RedirectIfAuthenticated() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (user) return <Navigate to={homePathFor(user.role)} replace />;
  return <Outlet />;
}

function FullPageSpinner() {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
      }}
    >
      <CircularProgress size={28} />
    </Box>
  );
}
